/**
 * DocumentRequirementsChecklist — R3
 *
 * Rendered above the existing document list in the Documents tab when the
 * `document_requirements_engine` feature flag is enabled for the tenant.
 *
 * Groups requirements by: Overdue → Pending → Sent → Received → Approved → Waived
 * "Overdue" is computed client-side: status ∈ {pending, sent} AND due_date < today.
 */
import { useState, useRef } from 'react';
import {
  RefreshCw,
  Plus,
  Send,
  Upload,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import {
  useProjectDocumentRequirements,
  useDocumentRequirementTypes,
  useAddManualRequirement,
  useGenerateRequirements,
  useSendRequirement,
  useApproveRequirement,
  useFulfillRequirement,
  useWaiveRequirement,
  useDeleteRequirement,
  useUpdateRequirement,
  type ProjectDocumentRequirement,
  type RequirementStatus,
} from '@/hooks/useDocumentRequirements';

// ============================================================================
// Constants
// ============================================================================

const RECIPIENT_ROLES = [
  { value: 'primary_client', label: 'Primary Client' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'general_contractor', label: 'General Contractor' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'internal_staff', label: 'Internal Staff' },
  { value: 'custom_email', label: 'Custom Email…' },
] as const;

const STATUS_ORDER: RequirementStatus[] = [
  'overdue',
  'pending',
  'sent',
  'received',
  'approved',
  'waived',
];

const STATUS_LABELS: Record<RequirementStatus, string> = {
  overdue: 'Overdue',
  pending: 'Pending',
  sent: 'Sent',
  received: 'Received',
  approved: 'Approved',
  waived: 'Waived',
};

const STATUS_COLORS: Record<RequirementStatus, string> = {
  overdue: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-blue-100 text-blue-700 border-blue-200',
  received: 'bg-purple-100 text-purple-700 border-purple-200',
  approved: 'bg-green-100 text-green-700 border-green-200',
  waived: 'bg-amber-100 text-amber-700 border-amber-200',
};

const STATUS_GROUP_HEADERS: Record<RequirementStatus, string> = {
  overdue: 'bg-red-50 text-red-800',
  pending: 'bg-gray-50 text-gray-700',
  sent: 'bg-blue-50 text-blue-800',
  received: 'bg-purple-50 text-purple-800',
  approved: 'bg-green-50 text-green-800',
  waived: 'bg-amber-50 text-amber-800',
};

// ============================================================================
// Helpers
// ============================================================================

function getDisplayStatus(req: ProjectDocumentRequirement): RequirementStatus {
  if (
    (req.status === 'pending' || req.status === 'sent') &&
    req.due_date &&
    new Date(req.due_date) < new Date(new Date().toDateString())
  ) {
    return 'overdue';
  }
  return req.status;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// Waive Dialog
// ============================================================================

interface WaiveDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}

function WaiveDialog({ open, onClose, onConfirm, isPending }: WaiveDialogProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) return;
    onConfirm(reason.trim());
    setReason('');
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Waive Requirement</AlertDialogTitle>
          <AlertDialogDescription>
            Provide a reason for waiving this requirement. This is required.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-2">
          <Label className="text-xs">Reason *</Label>
          <Input
            autoFocus
            placeholder="e.g. Client exemption confirmed by Jamie — email on file"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1"
            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!reason.trim() || isPending}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Waive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================================
// Edit Inline Panel
// ============================================================================

interface EditPanelProps {
  req: ProjectDocumentRequirement;
  projectId: string;
  onClose: () => void;
}

function EditPanel({ req, projectId, onClose }: EditPanelProps) {
  const update = useUpdateRequirement(projectId);
  const [dueDate, setDueDate] = useState(req.due_date ?? '');
  const [recipientRole, setRecipientRole] = useState(req.recipient_email ? 'custom_email' : '');
  const [customEmail, setCustomEmail] = useState(req.custom_recipient_email ?? '');

  const handleSave = async () => {
    await update.mutateAsync({
      id: req.id,
      due_date: dueDate || null,
      ...(customEmail ? { custom_recipient_email: customEmail } : {}),
    });
    onClose();
  };

  return (
    <div className="bg-muted/30 border-t px-3 py-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Due Date</Label>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-8 text-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Recipient Role</Label>
          <Select value={recipientRole} onValueChange={setRecipientRole}>
            <SelectTrigger className="h-8 text-sm mt-1">
              <SelectValue placeholder="Unchanged" />
            </SelectTrigger>
            <SelectContent>
              {RECIPIENT_ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {recipientRole === 'custom_email' && (
          <div className="col-span-2">
            <Label className="text-xs">Custom Recipient Email *</Label>
            <Input
              type="email"
              placeholder="recipient@example.com"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              className="h-8 text-sm mt-1"
              required
            />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={handleSave}
          disabled={
            update.isPending ||
            (recipientRole === 'custom_email' && !customEmail.trim())
          }
        >
          Save
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Requirement Row
// ============================================================================

interface RequirementRowProps {
  req: ProjectDocumentRequirement;
  displayStatus: RequirementStatus;
  projectId: string;
  isEditing: boolean;
  onToggleEdit: () => void;
}

function RequirementRow({
  req,
  displayStatus,
  projectId,
  isEditing,
  onToggleEdit,
}: RequirementRowProps) {
  const send = useSendRequirement(projectId);
  const approve = useApproveRequirement(projectId);
  const fulfill = useFulfillRequirement(projectId);
  const waive = useWaiveRequirement(projectId);
  const del = useDeleteRequirement(projectId);

  const [waiveOpen, setWaiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const typeName =
    req.requirement_type_name ?? req.requirement_type_code ?? 'Requirement';
  const recipientDisplay = req.recipient_email ?? req.custom_recipient_email ?? '—';
  const isManual = req.rule_id === null;

  const handleFulfillFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.documents.upload(
        file,
        file.name.replace(/\.[^.]+$/, ''),
        undefined,
        undefined,
        projectId,
      );
      const docId = result?.document?.id;
      if (!docId) throw new Error('Upload did not return a document ID');
      await fulfill.mutateAsync({ requirementId: req.id, documentId: docId });
    } catch (err: any) {
      // toast shown by hook
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <>
      <tr className="border-t hover:bg-muted/20 group">
        {/* Name */}
        <td className="px-3 py-2 font-medium text-sm">{typeName}</td>
        {/* Due date */}
        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
          {formatDate(req.due_date)}
        </td>
        {/* Recipient */}
        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[140px]">
          {recipientDisplay}
        </td>
        {/* Status pill */}
        <td className="px-3 py-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
              STATUS_COLORS[displayStatus],
            )}
          >
            {STATUS_LABELS[displayStatus]}
          </span>
        </td>
        {/* Last event */}
        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
          {req.sent_at
            ? `Sent ${formatDate(req.sent_at)}`
            : req.received_at
              ? `Received ${formatDate(req.received_at)}`
              : formatDate(req.created_at)}
        </td>
        {/* Actions */}
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Send now — pending/overdue only */}
            {(displayStatus === 'pending' || displayStatus === 'overdue') && (
              <button
                data-testid={`send-btn-${req.id}`}
                className="p-1 hover:bg-blue-100 rounded text-blue-600"
                title="Send now"
                onClick={() => send.mutate(req.id)}
                disabled={send.isPending}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Mark received + upload — sent only */}
            {displayStatus === 'sent' && (
              <>
                <button
                  data-testid={`fulfill-btn-${req.id}`}
                  className="p-1 hover:bg-purple-100 rounded text-purple-600"
                  title="Mark received + upload"
                  disabled={uploading || fulfill.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="w-3.5 h-3.5" />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  onChange={handleFulfillFile}
                />
              </>
            )}
            {/* Approve — received only */}
            {displayStatus === 'received' && (
              <button
                data-testid={`approve-btn-${req.id}`}
                className="p-1 hover:bg-green-100 rounded text-green-600"
                title="Approve"
                onClick={() => approve.mutate(req.id)}
                disabled={approve.isPending}
              >
                <CheckCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Edit — always */}
            {displayStatus !== 'waived' && displayStatus !== 'approved' && (
              <button
                data-testid={`edit-btn-${req.id}`}
                className="p-1 hover:bg-muted rounded text-muted-foreground"
                title="Edit due date / recipient"
                onClick={onToggleEdit}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Waive — not already waived/approved */}
            {displayStatus !== 'waived' && displayStatus !== 'approved' && (
              <button
                data-testid={`waive-btn-${req.id}`}
                className="p-1 hover:bg-amber-100 rounded text-amber-600"
                title="Waive…"
                onClick={() => setWaiveOpen(true)}
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Delete — manual rows only */}
            {isManual && (
              <button
                data-testid={`delete-btn-${req.id}`}
                className="p-1 hover:bg-destructive/10 hover:text-destructive rounded text-muted-foreground"
                title="Delete (manual requirement)"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Edit panel */}
      {isEditing && (
        <tr>
          <td colSpan={6} className="p-0">
            <EditPanel req={req} projectId={projectId} onClose={onToggleEdit} />
          </td>
        </tr>
      )}

      {/* Waive dialog */}
      <WaiveDialog
        open={waiveOpen}
        onClose={() => setWaiveOpen(false)}
        onConfirm={(reason) => {
          waive.mutate({ requirementId: req.id, reason });
          setWaiveOpen(false);
        }}
        isPending={waive.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Requirement</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{typeName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate(req.id)}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================================
// Status Group
// ============================================================================

interface StatusGroupProps {
  status: RequirementStatus;
  requirements: ProjectDocumentRequirement[];
  projectId: string;
  editingId: string | null;
  onToggleEdit: (id: string) => void;
}

function StatusGroup({
  status,
  requirements,
  projectId,
  editingId,
  onToggleEdit,
}: StatusGroupProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`status-group-${status}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium',
          STATUS_GROUP_HEADERS[status],
        )}
      >
        {status === 'overdue' && (
          <AlertCircle className="w-3.5 h-3.5 text-red-600" />
        )}
        <span>{STATUS_LABELS[status]}</span>
        <span className="text-xs opacity-70 ml-1">({requirements.length})</span>
        <ChevronDown
          className={cn(
            'w-3 h-3 opacity-60 ml-auto transition-transform',
            !isOpen && '-rotate-90',
          )}
        />
      </button>

      {isOpen && (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/20 text-xs text-muted-foreground">
              <th className="px-3 py-1.5 text-left">Requirement</th>
              <th className="px-3 py-1.5 text-left w-28">Due</th>
              <th className="px-3 py-1.5 text-left w-36">Recipient</th>
              <th className="px-3 py-1.5 w-24"></th>
              <th className="px-3 py-1.5 text-left w-36">Last event</th>
              <th className="px-3 py-1.5 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {requirements.map((req) => (
              <RequirementRow
                key={req.id}
                req={req}
                displayStatus={status}
                projectId={projectId}
                isEditing={editingId === req.id}
                onToggleEdit={() => onToggleEdit(req.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================================
// Add Manual Requirement Form
// ============================================================================

interface AddFormProps {
  projectId: string;
  onClose: () => void;
}

function AddForm({ projectId, onClose }: AddFormProps) {
  const { data: types = [] } = useDocumentRequirementTypes();
  const add = useAddManualRequirement(projectId);

  const [typeId, setTypeId] = useState('');
  const [recipientRole, setRecipientRole] = useState('primary_client');
  const [customEmail, setCustomEmail] = useState('');
  const [dueDate, setDueDate] = useState('');

  const handleSubmit = async () => {
    if (!typeId) return;
    if (recipientRole === 'custom_email' && !customEmail.trim()) return;
    await add.mutateAsync({
      requirement_type_id: typeId,
      recipient_role: recipientRole,
      ...(recipientRole === 'custom_email' ? { custom_recipient_email: customEmail.trim() } : {}),
      ...(dueDate ? { due_date: dueDate } : {}),
    });
    onClose();
  };

  const activeTypes = types.filter((t) => t.active);

  return (
    <div className="border border-dashed border-blue-300 rounded-lg p-4 space-y-3 mt-3">
      <p className="text-xs font-medium text-muted-foreground">Add manual requirement</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Requirement Type *</Label>
          <Select value={typeId} onValueChange={setTypeId}>
            <SelectTrigger className="h-8 text-sm mt-1" data-testid="add-type-select">
              <SelectValue placeholder="Select type…" />
            </SelectTrigger>
            <SelectContent>
              {activeTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.display_name ?? t.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Recipient Role *</Label>
          <Select value={recipientRole} onValueChange={setRecipientRole}>
            <SelectTrigger className="h-8 text-sm mt-1" data-testid="add-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECIPIENT_ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Due Date</Label>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-8 text-sm mt-1"
          />
        </div>
        {recipientRole === 'custom_email' && (
          <div className="col-span-2">
            <Label className="text-xs">Custom Recipient Email *</Label>
            <Input
              type="email"
              placeholder="recipient@example.com"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              className="h-8 text-sm mt-1"
              data-testid="add-custom-email-input"
              required
            />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-8"
          onClick={handleSubmit}
          disabled={
            !typeId ||
            add.isPending ||
            (recipientRole === 'custom_email' && !customEmail.trim())
          }
        >
          Add
        </Button>
        <Button variant="ghost" size="sm" className="h-8" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Main: DocumentRequirementsChecklist
// ============================================================================

interface DocumentRequirementsChecklistProps {
  projectId: string;
}

export function DocumentRequirementsChecklist({
  projectId,
}: DocumentRequirementsChecklistProps) {
  const { data: requirements = [], isLoading } = useProjectDocumentRequirements(projectId);
  const generate = useGenerateRequirements(projectId);
  const send = useSendRequirement(projectId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sendingAll, setSendingAll] = useState(false);

  const handleToggleEdit = (id: string) => {
    setEditingId((prev) => (prev === id ? null : id));
  };

  const handleSendAllPending = async () => {
    const pending = requirements.filter(
      (r) => r.status === 'pending' && !(r.due_date && new Date(r.due_date) < new Date()),
    );
    const overdue = requirements.filter(
      (r) =>
        (r.status === 'pending' || r.status === 'sent') &&
        r.due_date &&
        new Date(r.due_date) < new Date(),
    );
    const toSend = [...pending, ...overdue].filter(
      (r) => r.status === 'pending',
    );
    if (toSend.length === 0) return;
    setSendingAll(true);
    for (const req of toSend) {
      await send.mutateAsync(req.id);
      await new Promise((r) => setTimeout(r, 500));
    }
    setSendingAll(false);
  };

  // Group by display status
  const grouped = new Map<RequirementStatus, ProjectDocumentRequirement[]>();
  for (const status of STATUS_ORDER) {
    grouped.set(status, []);
  }
  for (const req of requirements) {
    const ds = getDisplayStatus(req);
    grouped.get(ds)!.push(req);
  }

  const pendingCount = (grouped.get('pending')?.length ?? 0) + (grouped.get('overdue')?.length ?? 0);
  const hasSendablePending = requirements.some(
    (r) => r.status === 'pending',
  );

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        Loading requirements…
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-2 border-b mb-3" data-testid="document-requirements-checklist">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            Document Requirements
          </span>
          {requirements.length > 0 && (
            <span className="text-xs text-muted-foreground">
              ({requirements.length} total
              {pendingCount > 0 ? `, ${pendingCount} action needed` : ''})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {hasSendablePending && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleSendAllPending}
              disabled={sendingAll || send.isPending}
              data-testid="send-all-pending-btn"
            >
              <Send className="w-3 h-3 mr-1" />
              {sendingAll ? 'Sending…' : 'Send all pending'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            data-testid="refresh-formula-btn"
          >
            <RefreshCw className={cn('w-3 h-3 mr-1', generate.isPending && 'animate-spin')} />
            Refresh from formula
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setShowAddForm((v) => !v)}
            data-testid="add-requirement-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {requirements.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No requirements yet. Click "Refresh from formula" to generate them from the project
          type's rule set, or add one manually.
        </p>
      )}

      {/* Status groups */}
      {requirements.length > 0 && (
        <div className="space-y-2">
          {STATUS_ORDER.map((status) => {
            const reqs = grouped.get(status) ?? [];
            if (reqs.length === 0) return null;
            return (
              <StatusGroup
                key={status}
                status={status}
                requirements={reqs}
                projectId={projectId}
                editingId={editingId}
                onToggleEdit={handleToggleEdit}
              />
            );
          })}
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <AddForm projectId={projectId} onClose={() => setShowAddForm(false)} />
      )}
    </div>
  );
}
